// Dir. 2007/45/CE, testo consolidato
// (https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:32007L0045),
// Allegato: il punto 1 porta la TABELLA delle gamme, il punto 2 le
// definizioni con i codici della nomenclatura combinata (NC). Il documento
// di conformita' citava il punto 2 per la tabella: e' il punto 1.
//
// Art. 2 §1: principio generale di liberta' dei formati. Le gamme restano
// obbligatorie solo per le sei categorie sotto, l'eccezione degli artt. 3-4.
// L'esenzione transitoria per latte, burro, pasta secca, caffe' e zucchero
// bianco (art. 2 §2) e' scaduta l'11 ottobre 2012/2013: quelle categorie non
// compaiono in questo catalogo perche' non c'e' piu' nulla da vincolare.
export const PREPACKAGED_NOMINAL_RANGE_CATEGORIES = [
  'STILL_WINE',
  'YELLOW_WINE',
  'SPARKLING_WINE',
  'LIQUEUR_WINE',
  'AROMATISED_WINE',
  'SPIRIT_DRINKS',
] as const;

export type PrepackagedNominalRangeCategory = (typeof PREPACKAGED_NOMINAL_RANGE_CATEGORIES)[number];

/**
 * Gamme nominali obbligatorie residue, Allegato punto 1 dir. 2007/45/CE, in
 * millilitri. Copiata verbatim dal testo consolidato citato sopra, un
 * commento per riga con la categoria come la nomina la norma e il codice NC
 * del punto 2. `Record` (non `Partial`) perche' TypeScript deve rifiutare
 * l'omissione della prossima categoria, non farla cadere su un fallback.
 *
 * Correzione rispetto al documento di conformita': «vino spumante di
 * qualita'» e «champagne» non hanno righe separate nell'Allegato; rientrano
 * nell'unica voce SPARKLING_WINE (NC 2204 10).
 */
export const NOMINAL_QUANTITY_RANGES: Readonly<
  Record<PrepackagedNominalRangeCategory, readonly number[]>
> = {
  // "Still wine" (vino tranquillo), NC ex 2204.
  STILL_WINE: [100, 187, 250, 375, 500, 750, 1000, 1500],
  // "Yellow wine" (vin jaune), NC ex 2204 — unico formato ammesso.
  YELLOW_WINE: [620],
  // "Sparkling wine" (vino spumante, comprende quello di qualita' e lo
  // champagne), NC 2204 10.
  SPARKLING_WINE: [125, 200, 375, 750, 1500],
  // "Liqueur wine" (vino liquoroso), NC 2204 21 - 2204 29.
  LIQUEUR_WINE: [100, 200, 375, 500, 750, 1000, 1500],
  // "Aromatised wine" (vino aromatizzato), NC 2205.
  AROMATISED_WINE: [100, 200, 375, 500, 750, 1000, 1500],
  // "Spirit drinks" (bevande spiritose), NC 2208.
  SPIRIT_DRINKS: [100, 200, 350, 500, 700, 1000, 1500, 1750, 2000],
};

/** `true` quando la quantita' (in ml) e' uno dei formati ammessi per quella categoria. */
export function isNominalQuantityAllowed(
  category: PrepackagedNominalRangeCategory,
  milliliters: number,
): boolean {
  return NOMINAL_QUANTITY_RANGES[category].includes(milliliters);
}

export interface PrepackagedNearestAllowedNominalQuantities {
  readonly below: number | null;
  readonly above: number | null;
}

/**
 * I due formati ammessi piu' vicini a una quantita' (in ml), per dire
 * all'operatore cosa scegliere quando la quantita' inserita non e' in
 * gamma. Su un formato gia' ammesso, `below` e `above` coincidono con quel
 * formato. `null` quando non esiste un vicino da quel lato (sotto il piu'
 * piccolo o sopra il piu' grande della categoria).
 */
export function nearestAllowedNominalQuantities(
  category: PrepackagedNominalRangeCategory,
  milliliters: number,
): PrepackagedNearestAllowedNominalQuantities {
  const allowedQuantities = NOMINAL_QUANTITY_RANGES[category];
  const below = allowedQuantities
    .filter((allowedQuantity) => allowedQuantity <= milliliters)
    .at(-1);
  const above = allowedQuantities.find((allowedQuantity) => allowedQuantity >= milliliters);
  return { below: below ?? null, above: above ?? null };
}
